import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBrandingConfig } from '../BrandingConfigContext';
import {
  applyThemeVariables,
  DESIGN_THEME_STORAGE_KEY,
  DESIGN_THEMES,
  getDesignCopy,
  isValidDesignTheme,
  readInitialDesignTheme,
} from './themeConfig';

const DesignThemeContext = createContext(null);

export function DesignThemeProvider({ children }) {
  const { brandingConfig } = useBrandingConfig();
  const [theme, setThemeState] = useState(readInitialDesignTheme);

  const setTheme = useCallback(nextTheme => {
    if (!isValidDesignTheme(nextTheme)) {
      return;
    }
    setThemeState(nextTheme);
    if (typeof window !== 'undefined') {
      localStorage.setItem(DESIGN_THEME_STORAGE_KEY, nextTheme);
    }
  }, []);

  useEffect(() => {
    applyThemeVariables(theme, brandingConfig);
  }, [theme, brandingConfig]);

  useEffect(() => {
    const onStorage = event => {
      if (event.key === DESIGN_THEME_STORAGE_KEY && isValidDesignTheme(event.newValue)) {
        setThemeState(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      isCorridor: theme === DESIGN_THEMES.CORRIDOR,
      isLegacy: theme === DESIGN_THEMES.LEGACY,
      copy: getDesignCopy(theme),
    }),
    [theme, setTheme],
  );

  return <DesignThemeContext.Provider value={value}>{children}</DesignThemeContext.Provider>;
}

export function useDesignTheme() {
  const context = useContext(DesignThemeContext);
  if (!context) {
    throw new Error('useDesignTheme must be used within DesignThemeProvider');
  }
  return context;
}
